"use strict";
import { ChevronDown, IconSize, Warning } from "../../icons";
import { KeyType, Layer, randomString } from "../../../utils";
import getOverlayPosition from "../../../utils/getOverlayPosition";
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import observeRect from "@reach/observe-rect";
import { Transition } from "react-transition-group";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";
import { createPortal } from "react-dom";
import classNames from "classnames";
import getPortalRoot from "../../../utils/getPortalRoot";
import { Text } from "../Text";
import Panel from "../Panel/Panel";
import { Label } from "../Label";
import { Expandable } from "../../behaviors/Expandable";
import { MenuItem } from "../MenuItem/MenuItem";
import { darkTheme, lightTheme } from "../../../tokens/tokens-css";
import {
  select,
  selectIcon,
  selectLabel,
  selectWrapper,
  selectElement,
  selectInput,
  selectMenu,
  selectPanel,
  selectError,
  selectErrorIcon,
  errorMessage
} from "./Select-css";
const SEARCH_BUFFER_CLEAR_TIMEOUT_MS = 200;
const SelectContext = createContext({});
const SelectTextSize = /* @__PURE__ */ new Map([
  ["l", "l"],
  ["m", "m"],
  ["s", "s"],
  ["xs", "xs"]
]);
export const Select = forwardRef(
  ({
    value,
    onChange,
    onClick,
    onClose,
    onFocus,
    onBlur,
    label,
    labelHidden,
    error,
    children,
    placeholder,
    size = "m",
    theme,
    multiselect,
    autoStack = true,
    className,
    name,
    required,
    disabled,
    icon,
    endElement,
    isNestedPortal = false
  }, ref) => {
    const [panelPosition, setPanelPosition] = useState();
    const [panelWidth, setPanelWidth] = useState();
    const [open, setOpen] = useState(false);
    const baseId = useRef(`select-${randomString()}`);
    const labelId = `${baseId.current}-label`;
    const inputId = `${baseId.current}-input`;
    const errorId = `${baseId.current}-error`;
    const menuId = `${baseId.current}-menu`;
    const internalInputRef = useRef(null);
    const inputRef = ref || internalInputRef;
    const menuRef = useRef(null);
    const focusPosition = useRef("first");
    const selectRoot = getPortalRoot("select-root");
    const optionsRef = useRef([]);
    const [displayValue, setDisplayValue] = useState();
    const searchBuffer = useRef();
    const searchBufferTimeout = useRef();
    useEffect(() => {
      const handleOuterClick = (event) => {
        var _a, _b;
        const targetNode = event.target;
        if (!((_a = menuRef.current) == null ? void 0 : _a.contains(targetNode)) && !((_b = inputRef.current) == null ? void 0 : _b.contains(targetNode))) {
          setOpen(false);
        }
      };
      const handleKeydown = (event) => {
        var _a;
        if (event.key === KeyType.Escape) {
          setOpen(false);
          (_a = inputRef.current) == null ? void 0 : _a.focus();
        }
      };
      if (open) {
        document.addEventListener("mousedown", handleOuterClick);
        document.addEventListener("keydown", handleKeydown);
      }
      return () => {
        document.removeEventListener("mousedown", handleOuterClick);
        document.removeEventListener("keydown", handleKeydown);
      };
    }, [inputRef, open]);
    useEffect(() => {
      if (!open || !inputRef.current)
        return;
      const rectObserver = observeRect(inputRef.current, (targetRect) => {
        var _a;
        const overlayRect = (_a = menuRef.current) == null ? void 0 : _a.getBoundingClientRect();
        const position = getOverlayPosition({
          position: "bottom",
          targetRect,
          overlayRect,
          alignment: "start",
          offset: 4
        });
        setPanelPosition(position);
        setPanelWidth(targetRect.width);
      });
      rectObserver.observe();
      return () => {
        rectObserver.unobserve();
      };
    }, [inputRef, open]);
    useEffect(() => {
      const getDisplayValue = () => {
        var _a, _b;
        if (!value || multiselect && (value == null ? void 0 : value.length) === 0)
          return placeholder;
        if (multiselect) {
          return value.length > 1 ? `${value.length} options selected` : (_a = optionsRef.current.find((option) => option.value === value[0])) == null ? void 0 : _a.label;
        }
        return (_b = optionsRef.current.find((option) => option.value === value)) == null ? void 0 : _b.label;
      };
      setDisplayValue(getDisplayValue());
    }, [multiselect, placeholder, value, children]);
    const getSelectedOptionIndex = () => {
      var _a;
      const buttons = Array.from(((_a = menuRef.current) == null ? void 0 : _a.getElementsByTagName("button")) || []);
      return buttons.indexOf(document.activeElement);
    };
    const handleInputClick = (event) => {
      setOpen(!open);
      focusPosition.current = "first";
      onClick == null ? void 0 : onClick(event);
    };
    const handleChange = (value2, keepOpenOnSelect) => {
      var _a;
      onChange(value2);
      if (!multiselect && !keepOpenOnSelect) {
        setOpen(false);
        (_a = inputRef.current) == null ? void 0 : _a.focus();
      }
    };
    const setFocus = (position) => {
      var _a, _b;
      const buttons = Array.from(((_a = menuRef.current) == null ? void 0 : _a.getElementsByTagName("button")) || []);
      const currentIndex = buttons.indexOf(document.activeElement);
      const length = buttons.length;
      const prevIndex = (currentIndex - 1 + length) % length;
      const nextIndex = (currentIndex + 1) % length;
      const firstFocusable = buttons[0];
      const lastFocusable = buttons[length - 1];
      const previousFocusable = buttons[prevIndex];
      const nextFocusable = buttons[nextIndex];
      switch (position) {
        case "first":
          firstFocusable == null ? void 0 : firstFocusable.focus();
          break;
        case "last":
          lastFocusable == null ? void 0 : lastFocusable.focus();
          break;
        case "next":
          nextFocusable == null ? void 0 : nextFocusable.focus();
          break;
        case "prev":
          previousFocusable == null ? void 0 : previousFocusable.focus();
          break;
        default:
          (_b = buttons[position]) == null ? void 0 : _b.focus();
      }
    };
    const handleInputKeyDown = (event) => {
      switch (event.key) {
        case KeyType.ArrowDown:
          event.preventDefault();
          setOpen(true);
          focusPosition.current = "first";
          break;
        case KeyType.ArrowUp:
          event.preventDefault();
          setOpen(true);
          focusPosition.current = "last";
          break;
      }
    };
    const jumpToMatch = useCallback(
      (prefix) => {
        if (!optionsRef.current)
          return;
        prefix = prefix.toLocaleLowerCase();
        const options = optionsRef.current;
        const currentIndex = getSelectedOptionIndex();
        let i = (currentIndex + 1) % options.length;
        while (i !== currentIndex) {
          if (options[i].label.toLocaleLowerCase().startsWith(prefix)) {
            setFocus(i);
            return;
          }
          i = (i + 1) % options.length;
        }
      },
      [optionsRef]
    );
    const clearBuffer = useCallback(() => {
      searchBuffer.current = "";
      searchBufferTimeout.current = void 0;
    }, [searchBuffer, searchBufferTimeout]);
    const handleListKeyDown = useCallback(
      (event) => {
        const { key } = event;
        const buffer = (searchBuffer.current || "") + key;
        searchBuffer.current = buffer;
        if (searchBufferTimeout.current !== void 0) {
          clearTimeout(searchBufferTimeout.current);
        }
        searchBufferTimeout.current = setTimeout(clearBuffer, SEARCH_BUFFER_CLEAR_TIMEOUT_MS);
        jumpToMatch(buffer);
      },
      [searchBuffer, searchBufferTimeout, clearBuffer, jumpToMatch]
    );
    const registerOption = useCallback((option) => {
      const newOptions = optionsRef.current.filter((optionRef) => optionRef.value !== option.value);
      newOptions.push(option);
      optionsRef.current = newOptions;
    }, []);
    const unregisterOption = useCallback((option) => {
      const newOptions = optionsRef.current.filter((optionRef) => optionRef.value !== option.value);
      optionsRef.current = newOptions;
    }, []);
    const renderStartElement = !!icon;
    return /* @__PURE__ */ React.createElement(
      SelectContext.Provider,
      {
        value: {
          onChange: handleChange,
          value,
          setFocus,
          size,
          multiselect,
          registerOption,
          unregisterOption,
          theme
        }
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames(
            select({ size }),
            {
              [lightTheme]: theme === "light",
              [darkTheme]: theme === "dark"
            },
            className
          )
        },
        /* @__PURE__ */ React.createElement(
          Label,
          {
            long: true,
            className: selectLabel,
            size: size === "xs" ? "s" : "m",
            id: labelId,
            hidden: labelHidden,
            htmlFor: inputId,
            theme,
            element: "label",
            disabled
          },
          label
        ),
        /* @__PURE__ */ React.createElement(
          Text,
          {
            className: selectWrapper({
              hasIcon: !!icon,
              placeholder: multiselect ? !(value == null ? void 0 : value.length) : !value
            }),
            size: SelectTextSize.get(size),
            lineHeight: "compact",
            element: "div",
            theme
          },
          renderStartElement && /* @__PURE__ */ React.createElement("div", { className: selectElement({ start: true, disabled }) }, /* @__PURE__ */ React.createElement("div", { className: selectIcon }, icon)),
          /* @__PURE__ */ React.createElement(
            "input",
            {
              className: selectInput({ size, hasIcon: !!icon }),
              type: "button",
              value: displayValue,
              id: inputId,
              "aria-labelledby": labelId,
              onClick: handleInputClick,
              onFocus,
              onBlur,
              onKeyDown: handleInputKeyDown,
              ref: inputRef,
              name,
              required,
              "aria-controls": menuId,
              "aria-expanded": open,
              "aria-haspopup": "listbox",
              "aria-describedby": error ? errorId : void 0,
              "aria-invalid": !!error,
              disabled
            }
          ),
          /* @__PURE__ */ React.createElement("div", { className: selectElement({ end: true, hasStart: renderStartElement, disabled }) }, !!endElement && endElement, /* @__PURE__ */ React.createElement("div", { className: selectIcon }, /* @__PURE__ */ React.createElement(ChevronDown, { "aria-hidden": true, size: size === "xs" ? IconSize.Small : IconSize.Medium })))
        ),
        /* @__PURE__ */ React.createElement(
          Transition,
          {
            in: open,
            timeout: AnimationDuration.Medium,
            onEnter: forceReflow,
            onExit: onClose,
            onEntering: () => setFocus(focusPosition.current)
          },
          (status) => createPortal(
            /* @__PURE__ */ React.createElement(Layer, null, ({ zIndex }) => /* @__PURE__ */ React.createElement(
              "div",
              {
                className: classNames(selectMenu({ status: status !== "exited" ? void 0 : status }), {
                  "nested-portal": isNestedPortal
                }),
                style: {
                  "--x": `${panelPosition == null ? void 0 : panelPosition.overlay.left}px`,
                  "--y": `${panelPosition == null ? void 0 : panelPosition.overlay.top}px`,
                  "--width": `${panelWidth}px`,
                  zIndex: autoStack ? zIndex : void 0
                },
                ref: menuRef,
                onMouseDown: (event) => {
                  event.nativeEvent.stopPropagation();
                  event.nativeEvent.stopImmediatePropagation();
                },
                onKeyDown: (event) => handleListKeyDown(event)
              },
              /* @__PURE__ */ React.createElement(
                Panel,
                {
                  className: selectPanel({
                    status: ["entering", "entered"].includes(status) ? status : void 0,
                    size: size === "l" ? "l" : void 0
                  }),
                  role: "listbox",
                  theme,
                  id: menuId
                },
                /* @__PURE__ */ React.createElement("div", { tabIndex: 0, onFocus: () => setFocus("last") }),
                children,
                /* @__PURE__ */ React.createElement("div", { tabIndex: 0, onFocus: () => setFocus("first") })
              )
            )),
            selectRoot || document.body
          )
        ),
        /* @__PURE__ */ React.createElement(Expandable, { expanded: !!error, timeout: AnimationDuration.Short, "aria-live": "polite", id: errorId }, ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { className: selectError, ref: expandableElementRef }, /* @__PURE__ */ React.createElement(Warning, { "aria-hidden": true, className: selectErrorIcon, size: IconSize.Small }), /* @__PURE__ */ React.createElement(Text, { size: "s", className: errorMessage }, error)))
      )
    );
  }
);
const SelectOptionSize = /* @__PURE__ */ new Map([
  ["l", "m"],
  ["m", "m"],
  ["s", "s"],
  ["xs", "xs"]
]);
export const SelectOption = ({
  onClick,
  children,
  value: optionValue,
  icon,
  endElement,
  keepOpenOnSelect = false
}) => {
  const {
    onChange,
    value: currentValue,
    setFocus,
    size,
    multiselect,
    registerOption,
    theme
  } = useContext(SelectContext);
  const selected = multiselect ? currentValue == null ? void 0 : currentValue.includes(optionValue) : currentValue === optionValue;
  useEffect(() => {
    const selectOption = { label: children, value: optionValue };
    registerOption(selectOption);
    return () => {
      registerOption(selectOption);
    };
  }, [children, optionValue, registerOption]);
  const getValue = () => {
    if (multiselect && typeof currentValue !== "string") {
      const value = currentValue || [];
      const newValue = selected ? value.filter((option) => option !== optionValue) : [...value, optionValue];
      return newValue;
    }
    return optionValue;
  };
  const handleClick = (event) => {
    const value = getValue();
    onClick == null ? void 0 : onClick(event);
    onChange(value, keepOpenOnSelect);
  };
  const handleKeyDown = (event) => {
    switch (event.key) {
      case KeyType.ArrowDown:
        event.preventDefault();
        setFocus("next");
        break;
      case KeyType.ArrowUp:
        event.preventDefault();
        setFocus("prev");
        break;
    }
  };
  return /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      className: classNames({
        [darkTheme]: theme === "dark",
        [lightTheme]: theme === "light"
      }),
      role: "option",
      selected,
      startElement: icon,
      endElement,
      size: SelectOptionSize.get(size),
      onClick: handleClick,
      onKeyDown: handleKeyDown
    },
    children
  );
};
